import { MapFacadeMaplibre } from "src/managers/MapFacadeMaplibre";
import { feature, featureCollection } from "@turf/helpers";
import { bbox, bboxPolygon, difference } from "@turf/turf";
import { setupJPBMapLayers } from "./JPBMapLayers";
import type { JPBReferenceProperty } from "./JPBState";
import { PriceMode, AreaUnit } from "./JPBState";
import { JPBManager } from "./JPBManager";
import type { GeoJSONSource, Map as MaplibreMap } from "maplibre-gl";
import { FeatureLayer } from "src/managers/WmsLayerFunc";
import { toPng } from "html-to-image";

const MASK_SOURCE_ID = "jpb-export-search-area-mask-source";
const MASK_LAYER_ID = "jpb-export-search-area-mask";

type RenderJPBOverviewImageOptions = {
   mapStyle: any;
   referenceProperties: JPBReferenceProperty[];
   checkedDocumentIds: string[];
   bufferGeometry?: any | null;
   /**
    * Optional 1-based row index per checked transaction (`documentID → index`).
    * When provided, an extra symbol layer is rendered with these numbers at
    * the upper-left corner of each green circle so the printed map can be
    * cross-referenced with the transactions table (Bilag 3).
    */
   reportRowIndices?: Map<string, number>;
};

const ROW_INDEX_SOURCE_ID = "jpb-export-row-index";
const ROW_INDEX_LAYER_ID = "jpb-export-row-index-labels";

// ============================================================================
// MARK: OVERVIEW WITH JORDBUND IMAGE (Bilag 2)
// ============================================================================

export async function renderJPBOverviewWithJordbundImage(
   options: RenderJPBOverviewImageOptions,
): Promise<Uint8Array | undefined> {
   const {
      mapStyle,
      referenceProperties,
      checkedDocumentIds,
      bufferGeometry,
      reportRowIndices,
   } = options;

   const checkedProperties = referenceProperties.filter((prop) =>
      checkedDocumentIds.includes(prop.documentID),
   );

   if (!checkedProperties.length) return undefined;

   const { exportMap, container, exportMapFacade } =
      await createExportMap(mapStyle);

   try {
      await fitMapToFeatures(exportMap, checkedProperties, { bufferGeometry });
      const currentZoom = exportMap.getZoom();
      const dynamicMinZoom = Math.min(currentZoom, 11);

      const jordbundLayer = new FeatureLayer({
         version: "1.3.0",
         label: "Jordbundskort_2024",
         host: "geodata.fvm.dk",
         path: "/geoserver/ows",
         layers: "Jordbunds_og_terraenforhold:Jordbundskort_2024",
         minZoom: dynamicMinZoom,
         maxZoom: 24,
      });

      exportMapFacade.addFeatureLayer("Jordbundskort_2024", jordbundLayer);
      await new Promise<void>((resolve) => setTimeout(() => resolve(), 200));

      const checked = new Set<string>(checkedDocumentIds);
      setupJPBMapLayers(
         exportMap,
         checkedProperties,
         checked,
         PriceMode.Standard,
         AreaUnit.SquareMeter,
         {
            onFeatureClick: () => {
               /* no-op in export map */
            },
         },
      );

      if (bufferGeometry) {
         addBufferLayer(exportMap, bufferGeometry);
         setupMaskLayer(exportMap, bufferGeometry);
      }

      if (reportRowIndices && reportRowIndices.size > 0) {
         addRowIndexLayer(exportMap, checkedProperties, reportRowIndices);
      }

      await waitForMapReady(exportMap, { timeoutMs: 10000, settleMs: 1000 });

      const mapCanvas = exportMap.getCanvas();
      const jordbundLegendImg = await loadJordbundLegendImage();
      if (!jordbundLegendImg) {
         return await captureMapImage(exportMap);
      }
      const legendSize = measureJordbundLegendSize(jordbundLegendImg);
      const { canvas: compositeCanvas, ctx: compositeCtx } = createCompositeCanvas(
         mapCanvas,
         legendSize.width + LEGEND_GAP,
         0,
      );
      drawJordbundLegendAt(
         compositeCtx,
         mapCanvas.width + LEGEND_GAP,
         10,
         jordbundLegendImg,
      );
      return await captureCanvasImage(compositeCanvas);
   } finally {
      cleanupExportMap(exportMapFacade, container);
   }
}

export async function renderJPBLandsdelGeometryImage(options: {
   mapStyle: any;
   features: any[];
}): Promise<Uint8Array | undefined> {
   const { mapStyle, features } = options;
   if (!mapStyle || !features.length) return undefined;

   const featureCollectionData = featureCollection(features as any);
   const { exportMap, container, exportMapFacade } = await createExportMap(
      mapStyle,
      { width: 544, height: 300 },
   );

   try {
      const bounds = bbox(featureCollectionData as any);
      exportMap.fitBounds(bounds as any, {
         padding: 42,
         maxZoom: 10,
      });

      const SOURCE_ID = "jpb-index-landsdel-preview";
      const FILL_ID = "jpb-index-landsdel-preview-fill";
      const OUTLINE_ID = "jpb-index-landsdel-preview-outline";

      exportMap.addSource(SOURCE_ID, {
         type: "geojson",
         data: featureCollectionData,
      } as any);

      exportMap.addLayer({
         id: FILL_ID,
         type: "fill",
         source: SOURCE_ID,
         paint: {
            "fill-color": "#f2b705",
            "fill-opacity": 0.38,
         },
      } as any);

      exportMap.addLayer({
         id: OUTLINE_ID,
         type: "line",
         source: SOURCE_ID,
         paint: {
            "line-color": "#8f3f00",
            "line-width": 2.5,
            "line-opacity": 0.95,
         },
      } as any);

      await waitForMapReady(exportMap, { timeoutMs: 10000, settleMs: 700 });
      return await captureMapImage(exportMap);
   } finally {
      cleanupExportMap(exportMapFacade, container);
   }
}

/**
 * Adds a top-most symbol layer that draws the row index (1-based) at the
 * upper-left corner of every checked transaction's green circle. The text
 * anchors at `bottom-right` so the label sits up-and-left of the point with
 * a tiny overlap, matching the requested layout in the report. This layer
 * is only added inside the export map; the live `JPBMapLayers` is untouched.
 */
function addRowIndexLayer(
   map: MaplibreMap,
   checkedProperties: JPBReferenceProperty[],
   reportRowIndices: Map<string, number>,
) {
   const features = checkedProperties
      .map((p) => {
         const idx = reportRowIndices.get(p.documentID);
         if (idx === undefined) return null;
         if (
            !p.coordinates ||
            !Number.isFinite(p.coordinates.lat) ||
            !Number.isFinite(p.coordinates.lng)
         ) {
            return null;
         }
         return {
            type: "Feature" as const,
            geometry: {
               type: "Point" as const,
               coordinates: [p.coordinates.lng, p.coordinates.lat],
            },
            properties: { rowIndex: idx },
         };
      })
      .filter((f): f is NonNullable<typeof f> => f !== null);

   if (features.length === 0) return;

   const data = { type: "FeatureCollection" as const, features };

   if (!map.getSource(ROW_INDEX_SOURCE_ID)) {
      map.addSource(ROW_INDEX_SOURCE_ID, { type: "geojson", data } as any);
   } else {
      const source = map.getSource(ROW_INDEX_SOURCE_ID) as GeoJSONSource;
      source.setData(data as any);
   }

   if (!map.getLayer(ROW_INDEX_LAYER_ID)) {
      map.addLayer({
         id: ROW_INDEX_LAYER_ID,
         type: "symbol",
         source: ROW_INDEX_SOURCE_ID,
         layout: {
            "text-field": ["to-string", ["get", "rowIndex"]],
            "text-size": 11,
            "text-font": [
               "Noto Sans Bold",
               "Open Sans Bold",
               "Arial Unicode MS Bold",
            ],
            // Anchor sits on the bottom-right of the label, placing the label
            // up-and-left of the point with the small offset below.
            "text-anchor": "bottom-right",
            "text-offset": [0.35, -0.35],
            "text-allow-overlap": true,
            "text-ignore-placement": true,
         },
         paint: {
            "text-color": "#1a1a1a",
            "text-halo-color": "#ffffff",
            "text-halo-width": 1.5,
         },
      } as any);
   }
}

// ============================================================================
// MARK: HISTOGRAM IMAGE
// ============================================================================

/**
 * Snapshot of a histogram SVG taken from the live DOM. Captured synchronously
 * at the start of report generation so the histogram can be rendered later
 * without depending on the live element still being attached / sized.
 */
export interface SerializedHistogramSnapshot {
   svgMarkup: string;
   width: number;
   height: number;
}

/**
 * Synchronously serializes a histogram SVG to a string + dimensions. Must be
 * called while the live element is still rendered. The returned snapshot is
 * fully self-contained and can be rasterized at any later point.
 */
export function serializeHistogramSvg(
   histogramElement: HTMLElement,
): SerializedHistogramSnapshot | undefined {
   const svgElement = histogramElement.querySelector("svg");
   if (!svgElement) {
      console.warn("Could not find SVG element in histogram");
      return undefined;
   }
   const rect = svgElement.getBoundingClientRect();
   const width = rect.width || 800;
   const height = rect.height || 400;
   const svgMarkup = new XMLSerializer().serializeToString(svgElement);
   return { svgMarkup, width, height };
}

/**
 * Rasterizes a previously-serialized histogram snapshot into a PNG byte array.
 * Decoupled from the live DOM so it can run after the user has closed /
 * minimized the JPB window.
 */
export async function renderHistogramFromSerialized(
   snapshot: SerializedHistogramSnapshot,
): Promise<Uint8Array | undefined> {
   const canvas = document.createElement("canvas");
   canvas.width = snapshot.width;
   canvas.height = snapshot.height;
   const ctx = canvas.getContext("2d");
   if (!ctx) {
      console.warn("Could not get canvas context");
      return undefined;
   }

   ctx.fillStyle = "#ffffff";
   ctx.fillRect(0, 0, canvas.width, canvas.height);

   const svgBlob = new Blob([snapshot.svgMarkup], {
      type: "image/svg+xml;charset=utf-8",
   });
   const url = URL.createObjectURL(svgBlob);

   try {
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
         img.onload = () => resolve();
         img.onerror = () => reject(new Error("Failed to load SVG image"));
         img.src = url;
      });
      ctx.drawImage(img, 0, 0);
   } finally {
      URL.revokeObjectURL(url);
   }

   return await captureCanvasImage(canvas);
}

/**
 * Legacy: rasterize a histogram directly from its live DOM element. Kept for
 * backwards compatibility with any non-report callers; new code should
 * prefer {@link serializeHistogramSvg} + {@link renderHistogramFromSerialized}
 * so capture is decoupled from the live UI.
 */
export async function renderJPBHistogramImage(
   histogramElement: HTMLElement,
): Promise<Uint8Array | undefined> {
   const snapshot = serializeHistogramSvg(histogramElement);
   if (!snapshot) return undefined;
   return await renderHistogramFromSerialized(snapshot);
}

// ============================================================================
// MARK: HELPER FUNCTIONS
// ============================================================================

async function createExportMap(
   mapStyle: any,
   size: { width: number; height: number } = { width: 800, height: 600 },
): Promise<{
   exportMap: MaplibreMap;
   container: HTMLDivElement;
   exportMapFacade: MapFacadeMaplibre;
}> {
   const container = document.createElement("div");
   // Position the export map at viewport (0, 0) and shift it off-screen with
   // a CSS transform. Using `left/top: -99999px` can cause some browsers /
   // GPU drivers to skip rasterizing the WebGL canvas because it is far
   // outside the viewport coordinate space, producing blank screenshots.
   // A `transform: translate()` keeps the canvas at the layout origin while
   // visually shifting it off-screen, preserving GPU rasterization.
   container.style.position = "fixed";
   container.style.top = "0";
   container.style.left = "0";
   container.style.transform = "translate(-200vw, 0)";
   container.style.width = `${size.width}px`;
   container.style.height = `${size.height}px`;
   container.style.pointerEvents = "none";
   container.style.zIndex = "-1";
   container.setAttribute("aria-hidden", "true");
   document.body.appendChild(container);

   const exportMapFacade = new MapFacadeMaplibre(container, false, {
      mapStyle,
      interactive: false,
      hash: false,
      canvasContextAttributes: {
         preserveDrawingBuffer: true,
         powerPreference: "high-performance",
      },
   });

   const exportMap = exportMapFacade.getMapPop();

   await new Promise<void>((resolve) => {
      if ((exportMap as any).isStyleLoaded?.()) {
         resolve();
      } else {
         exportMap.once("load", () => resolve());
      }
   });

   return { exportMap, container, exportMapFacade };
}

function cleanupExportMap(
   exportMapFacade: MapFacadeMaplibre | undefined,
   container: HTMLDivElement,
) {
   if (exportMapFacade) {
      try {
         exportMapFacade.getMapPop().remove();
      } catch {
         // ignore
      }
   }
   if (container.parentNode) {
      container.parentNode.removeChild(container);
   }
}

function addBufferLayer(map: MaplibreMap, bufferGeometry: any) {
   const SOURCE_ID = "jpb-export-search-area";
   const OUTLINE_ID = "jpb-export-search-area-outline";
   const combinedBufferGeometry =
      JPBManager.toCombinedSearchGeometry(bufferGeometry) || bufferGeometry;

   if (!map.getSource(SOURCE_ID)) {
      map.addSource(SOURCE_ID, {
         type: "geojson",
         data: combinedBufferGeometry,
      } as any);
   }

   if (!map.getLayer(OUTLINE_ID)) {
      map.addLayer({
         id: OUTLINE_ID,
         type: "line",
         source: SOURCE_ID,
         paint: {
            "line-color": "#d6c251",
            "line-width": 2,
         },
      } as any);
   }
}

const MAP_FIT_PADDING = 40;
const LEGEND_GAP = 12;

function isValidReportCoordinate(lng: number, lat: number): boolean {
   return (
      Number.isFinite(lng) &&
      Number.isFinite(lat) &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180 &&
      !(lng === 0 && lat === 0)
   );
}

async function fitMapToFeatures(
   map: MaplibreMap,
   properties: JPBReferenceProperty[],
   options: { bufferGeometry?: any | null } = {},
) {
   const displayCoordsById =
      JPBManager.getJpbDisplayPointCoordinatesByDocumentId(properties);
   const features = properties.flatMap((prop) => {
      const pt = displayCoordsById.get(prop.documentID);
      const lng = pt?.[0] ?? prop.coordinates.lng;
      const lat = pt?.[1] ?? prop.coordinates.lat;
      if (!isValidReportCoordinate(lng, lat)) return [];
      return [
         feature({
            type: "Point",
            coordinates: [lng, lat],
         }),
      ];
   });

   const searchAreaFeature = options.bufferGeometry
      ? JPBManager.toCombinedSearchGeometry(options.bufferGeometry)
      : null;
   if (searchAreaFeature) {
      features.push(searchAreaFeature);
   }

   if (!features.length) return;

   let bounds = bbox(featureCollection(features as any));
   const [west, south, east, north] = bounds;
   if (west === east || south === north) {
      const pad = 0.005;
      bounds = [west - pad, south - pad, east + pad, north + pad];
   }

   map.fitBounds(bounds as any, {
      padding: MAP_FIT_PADDING,
      maxZoom: 16,
   });

   await waitForMapReady(map);
}

function createCompositeCanvas(
   mapCanvas: HTMLCanvasElement,
   extraWidth = 0,
   extraHeight = 0,
): {
   canvas: HTMLCanvasElement;
   ctx: CanvasRenderingContext2D;
} {
   const canvas = document.createElement("canvas");
   canvas.width = mapCanvas.width + extraWidth;
   canvas.height = mapCanvas.height + extraHeight;
   const ctx = canvas.getContext("2d");
   if (!ctx) {
      throw new Error("Failed to create composite canvas context");
   }
   ctx.fillStyle = "#ffffff";
   ctx.fillRect(0, 0, canvas.width, canvas.height);
   ctx.drawImage(mapCanvas, 0, 0);
   return { canvas, ctx };
}

/**
 * Polls map readiness instead of relying on the `"idle"` event.
 *
 * Why polling: the `"idle"` event fires from MapLibre's render loop, which is
 * driven by `requestAnimationFrame`. In background / minimized tabs, browsers
 * throttle `requestAnimationFrame` to ~1Hz or pause it entirely, so the
 * previous `await map.once("idle")` could effectively hang indefinitely and
 * produce blank screenshots when the user moved focus away during report
 * generation. `setTimeout` is throttled but never paused, so this loop makes
 * forward progress regardless of tab visibility — combined with explicit
 * `triggerRepaint()` calls to keep MapLibre rasterizing into the canvas.
 */
async function waitForMapReady(
   map: MaplibreMap,
   options: { timeoutMs?: number; settleMs?: number; pollMs?: number } = {},
): Promise<void> {
   const timeoutMs = options.timeoutMs ?? 8000;
   const settleMs = options.settleMs ?? 250;
   const pollMs = options.pollMs ?? 100;
   const start = Date.now();

   const isReady = (): boolean => {
      try {
         const styleLoaded = (map as any).isStyleLoaded?.() ?? true;
         const tilesLoaded = (map as any).areTilesLoaded?.() ?? true;
         const moving = (map as any).isMoving?.() ?? false;
         const zooming = (map as any).isZooming?.() ?? false;
         return styleLoaded && tilesLoaded && !moving && !zooming;
      } catch {
         return true;
      }
   };

   while (Date.now() - start < timeoutMs) {
      try {
         map.triggerRepaint();
      } catch {
         /* ignore */
      }
      if (isReady()) {
         await new Promise((r) => setTimeout(r, settleMs));
         try {
            map.triggerRepaint();
         } catch {
            /* ignore */
         }
         if (isReady()) return;
      }
      await new Promise((r) => setTimeout(r, pollMs));
   }
   console.warn(
      "waitForMapReady timed out - proceeding with whatever the canvas currently holds",
   );
}

async function captureMapImage(
   map: MaplibreMap,
): Promise<Uint8Array | undefined> {
   const canvas = map.getCanvas();
   const blob: Blob | null = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/png");
   });
   if (!blob) return undefined;
   const buf = await blob.arrayBuffer();
   return new Uint8Array(buf);
}

async function captureCanvasImage(
   canvas: HTMLCanvasElement,
): Promise<Uint8Array | undefined> {
   const blob: Blob | null = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/png");
   });
   if (!blob) return undefined;
   const buf = await blob.arrayBuffer();
   return new Uint8Array(buf);
}

const JPB_LEGEND_ENTRIES: Array<{
   label: string;
   circleColor?: string;
   overlay?: string;
   outline?: boolean;
}> = [
   { label: "Søgeområde", outline: true },
   { label: "Medregnes - Ren jordhandel", circleColor: "#3a8e87" },
   {
      label: "Medregnes - også handlet bygninger",
      circleColor: "#3a8e87",
      overlay: "h",
   },
   {
      label: "Medregnes ikke - Ren jordhandel - fjernet af regel",
      circleColor: "#606060",
   },
   {
      label: "Medregnes ikke - også handlet bygninger - fjernet af regel",
      circleColor: "#606060",
      overlay: "h",
   },
   {
      label:
         "Medregnes ikke - Mangelfuld information i skødet - fjernet af regel",
      circleColor: "#1f1f1f",
   },
   {
      label: "Medregnes ikke - også handlet bygninger - ikke handlet nok jord",
      circleColor: "#1f1f1f",
      overlay: "h",
   },
   {
      label: "Medregnes ikke - mangelfuld information i skødet",
      circleColor: "#1f1f1f",
      overlay: "!",
   },
];

function measureJPBLegendSize(): { width: number; height: number } {
   const ROW_HEIGHT = 22;
   const PAD_X = 14;
   const PAD_Y = 12;
   const SWATCH_SIZE = 18;
   const TEXT_GAP = 10;
   const canvas = document.createElement("canvas");
   const ctx = canvas.getContext("2d");
   if (!ctx) return { width: 420, height: 234 };
   ctx.font = "12px Arial, sans-serif";
   const textMetrics = JPB_LEGEND_ENTRIES.map((e) =>
      ctx.measureText(e.label).width,
   );
   ctx.font = "bold 13px Arial, sans-serif";
   const titleWidth = ctx.measureText("Signaturforklaring").width;
   const contentWidth =
      Math.max(titleWidth, ...textMetrics) + SWATCH_SIZE + TEXT_GAP;
   return {
      width: contentWidth + PAD_X * 2,
      height: PAD_Y * 2 + 22 + JPB_LEGEND_ENTRIES.length * ROW_HEIGHT,
   };
}

/** Draws the JPB legend below the map on a composite canvas (not over map data). */
function drawJPBLegendAt(
   ctx: CanvasRenderingContext2D,
   x: number,
   y: number,
): { width: number; height: number } {

   const ROW_HEIGHT = 22;
   const PAD_X = 14;
   const PAD_Y = 12;
   const SWATCH_SIZE = 18;
   const TEXT_GAP = 10;
   const FONT = "12px Arial, sans-serif";
   const TITLE_FONT = "bold 13px Arial, sans-serif";
   const { width: boxWidth, height: boxHeight } = measureJPBLegendSize();
   const titleText = "Signaturforklaring";

   ctx.save();
   ctx.fillStyle = "rgba(255,255,255,0.98)";
   ctx.beginPath();
   ctx.roundRect(x, y, boxWidth, boxHeight, 10);
   ctx.fill();

   ctx.strokeStyle = "rgba(0,0,0,0.12)";
   ctx.lineWidth = 1;
   ctx.beginPath();
   ctx.roundRect(x, y, boxWidth, boxHeight, 10);
   ctx.stroke();

   ctx.fillStyle = "#222";
   ctx.font = TITLE_FONT;
   ctx.textBaseline = "top";
   ctx.fillText(titleText, x + PAD_X, y + PAD_Y);

   ctx.font = FONT;
   JPB_LEGEND_ENTRIES.forEach((entry, i) => {
      const rowY = y + PAD_Y + 22 + i * ROW_HEIGHT;
      const swatchCx = x + PAD_X + SWATCH_SIZE / 2;
      const swatchCy = rowY + SWATCH_SIZE / 2;

      if (entry.outline) {
         ctx.strokeStyle = "#d6c251";
         ctx.lineWidth = 2;
         ctx.beginPath();
         ctx.arc(
            swatchCx,
            swatchCy,
            SWATCH_SIZE / 2 - 1,
            Math.PI * 0.5,
            Math.PI,
            false,
         );
         ctx.stroke();
      } else if (entry.circleColor) {
         ctx.fillStyle = entry.circleColor;
         ctx.beginPath();
         ctx.arc(swatchCx, swatchCy, SWATCH_SIZE / 2, 0, Math.PI * 2);
         ctx.fill();
         ctx.strokeStyle = "#ffffff";
         ctx.lineWidth = 2;
         ctx.stroke();
         if (entry.overlay) {
            ctx.fillStyle = "#ffffff";
            ctx.font = "bold 11px Arial, sans-serif";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(
               entry.overlay === "h" ? "⌂" : entry.overlay,
               swatchCx,
               swatchCy + 0.5,
            );
            ctx.font = FONT;
            ctx.textAlign = "left";
            ctx.textBaseline = "top";
         }
      }

      ctx.fillStyle = "#222";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(entry.label, x + PAD_X + SWATCH_SIZE + TEXT_GAP, swatchCy);
   });
   ctx.restore();
   return { width: boxWidth, height: boxHeight };
}

// ============================================================================
// MARK: HEATMAP IMAGE — price-colored circles + legend (Bilag 1)
// ============================================================================

type RenderJPBHeatmapImageOptions = {
   mapStyle: any;
   referenceProperties: JPBReferenceProperty[];
   checkedDocumentIds: string[];
   bufferGeometry?: any | null;
   priceRange?: { min: number; max: number };
};

export async function renderJPBHeatmapImage(
   options: RenderJPBHeatmapImageOptions,
): Promise<Uint8Array | undefined> {
   const {
      mapStyle,
      referenceProperties,
      checkedDocumentIds,
      bufferGeometry,
      priceRange,
   } = options;
   const checkedProperties = referenceProperties.filter((prop) =>
      checkedDocumentIds.includes(prop.documentID),
   );
   if (!checkedProperties.length) return undefined;

   const { exportMap, container, exportMapFacade } =
      await createExportMap(mapStyle);
   try {
      const { minPrice, maxPrice, minArea, maxArea } = calculateLegendValues(
         checkedProperties,
         priceRange,
      );
      setupPriceColoredCirclesLayer(exportMap, checkedProperties, priceRange);

      if (bufferGeometry) {
         addBufferLayer(exportMap, bufferGeometry);
         setupMaskLayer(exportMap, bufferGeometry);
      }

      await fitMapToFeatures(exportMap, checkedProperties, { bufferGeometry });
      await waitForMapReady(exportMap);

      const mapCanvas = exportMap.getCanvas();
      const legendSize = measurePriceLegendSize(minArea, maxArea);
      const { canvas: compositeCanvas, ctx: compositeCtx } = createCompositeCanvas(
         mapCanvas,
         legendSize.width + LEGEND_GAP,
         0,
      );
      drawPriceLegendAt(
         compositeCtx,
         mapCanvas.width + LEGEND_GAP,
         10,
         minPrice,
         maxPrice,
         minArea,
         maxArea,
      );
      return await captureCanvasImage(compositeCanvas);
   } finally {
      cleanupExportMap(exportMapFacade, container);
   }
}

function calculateLegendValues(
   properties: JPBReferenceProperty[],
   priceRange?: { min: number; max: number },
) {
   let minPrice = priceRange?.min;
   let maxPrice = priceRange?.max;
   if (minPrice === undefined || maxPrice === undefined) {
      const prices = properties
         .map((p) => p.pricePerSquareMeter)
         .filter((p) => p !== null && p !== undefined && !isNaN(p));
      if (prices.length > 0) {
         minPrice = minPrice ?? Math.min(...prices);
         maxPrice = maxPrice ?? Math.max(...prices);
      } else {
         minPrice = 0;
         maxPrice = 100;
      }
   }
   const areas = properties
      .map((p) => p.area)
      .filter((a) => a !== null && a !== undefined && !isNaN(a) && a > 0);
   const minArea = areas.length > 0 ? Math.min(...areas) : undefined;
   const maxArea = areas.length > 0 ? Math.max(...areas) : undefined;
   return { minPrice: minPrice!, maxPrice: maxPrice!, minArea, maxArea };
}

function setupPriceColoredCirclesLayer(
   map: MaplibreMap,
   properties: JPBReferenceProperty[],
   priceRange?: { min: number; max: number },
) {
   const SOURCE_ID = "jpb-export-price-circles";
   const LAYER_ID = "jpb-export-price-circles-layer";

   let minPrice = priceRange?.min;
   let maxPrice = priceRange?.max;
   if (minPrice === undefined || maxPrice === undefined) {
      const prices = properties
         .map((p) => p.pricePerSquareMeter)
         .filter((p) => p !== null && p !== undefined && !isNaN(p));
      if (prices.length > 0) {
         minPrice = minPrice ?? Math.min(...prices);
         maxPrice = maxPrice ?? Math.max(...prices);
      } else {
         minPrice = 0;
         maxPrice = 100;
      }
   }

   const areas = properties
      .map((p) => p.area)
      .filter((a) => a !== null && a !== undefined && !isNaN(a) && a > 0);
   const minArea = areas.length > 0 ? Math.min(...areas) : 0;
   const maxArea = areas.length > 0 ? Math.max(...areas) : 10000;
   const areaRangeSize = maxArea - minArea || 1;

   const displayCoordsById =
      JPBManager.getJpbDisplayPointCoordinatesByDocumentId(properties);

   const geoJson = {
      type: "FeatureCollection" as const,
      features: properties.flatMap((prop) => {
         const pt = displayCoordsById.get(prop.documentID);
         const lng = pt?.[0] ?? prop.coordinates.lng;
         const lat = pt?.[1] ?? prop.coordinates.lat;
         if (!isValidReportCoordinate(lng, lat)) return [];
         const price = prop.pricePerSquareMeter || minPrice;
         const area = prop.area || minArea;
         const color = getPriceColor(price, minPrice!, maxPrice!);
         const normalizedArea = Math.max(
            0,
            Math.min(1, (area - minArea) / areaRangeSize),
         );
         const radius = 5 + normalizedArea * 25;
         return [
            {
               id: prop.documentID,
               type: "Feature" as const,
               geometry: {
                  type: "Point" as const,
                  coordinates: [lng, lat],
               },
               properties: {
                  documentID: prop.documentID,
                  pricePerSquareMeter: price,
                  area,
                  color,
                  radius,
               },
            },
         ];
      }),
   };

   if (map.getSource(SOURCE_ID)) {
      const src = map.getSource(SOURCE_ID) as GeoJSONSource;
      if (src) src.setData(geoJson as any);
   } else {
      map.addSource(SOURCE_ID, {
         type: "geojson",
         promoteId: "documentID",
         data: geoJson as any,
      });
   }
   if (!map.getLayer(LAYER_ID)) {
      map.addLayer({
         id: LAYER_ID,
         type: "circle",
         source: SOURCE_ID,
         paint: {
            "circle-color": ["get", "color"],
            "circle-radius": ["get", "radius"],
            "circle-stroke-width": 1.5,
            "circle-stroke-color": "#ffffff",
            "circle-opacity": 0.85,
         },
      } as any);
   }
}

function getPriceColor(
   price: number,
   minPrice: number,
   maxPrice: number,
): string {
   const range = maxPrice - minPrice || 1;
   const t = Math.max(0, Math.min(1, (price - minPrice) / range));
   return `rgb(255, ${Math.round(255 * (1 - t))}, 0)`;
}

function measurePriceLegendSize(
   minArea?: number,
   maxArea?: number,
): { width: number; height: number } {
   const haveArea = minArea !== undefined && maxArea !== undefined;
   const sizeSectionHeight = haveArea ? 90 : 0;
   return {
      width: 240,
      height:
         12 +
         8 +
         20 +
         15 +
         (haveArea ? 8 : 0) +
         sizeSectionHeight +
         10,
   };
}

function drawPriceLegendAt(
   ctx: CanvasRenderingContext2D,
   x: number,
   y: number,
   minPrice: number,
   maxPrice: number,
   minArea?: number,
   maxArea?: number,
): { width: number; height: number } {
   const cornerRadius = 12;
   const legendWidth = 240;
   const colorBarHeight = 20;
   const maxCircleRadius = 18;
   const haveArea = minArea !== undefined && maxArea !== undefined;
   const sizeSectionHeight = haveArea ? 90 : 0;
   const totalHeight =
      12 +
      8 +
      colorBarHeight +
      15 +
      (haveArea ? 8 : 0) +
      sizeSectionHeight +
      10;

   const bgX = x;
   const bgY = y;

   ctx.fillStyle = "rgba(255,255,255,0.98)";
   ctx.beginPath();
   ctx.roundRect(bgX, bgY, legendWidth, totalHeight, cornerRadius);
   ctx.fill();
   ctx.strokeStyle = "rgba(0,0,0,0.1)";
   ctx.lineWidth = 1;
   ctx.beginPath();
   ctx.roundRect(bgX, bgY, legendWidth, totalHeight, cornerRadius);
   ctx.stroke();

   let currentY = bgY + 12 + 8;
   ctx.fillStyle = "#333";
   ctx.font = "12px Arial";
   ctx.textAlign = "left";
   ctx.textBaseline = "alphabetic";
   ctx.fillText("Pris pr. m²", bgX + 12, currentY);
   currentY += 18;

   const gradientX = bgX + 12;
   const gradientY = currentY;
   const gradientWidth = legendWidth - 24;
   const gradient = ctx.createLinearGradient(
      gradientX,
      gradientY,
      gradientX + gradientWidth,
      gradientY,
   );
   [
      { pos: 0, color: "rgb(255,255,0)" },
      { pos: 0.25, color: "rgb(255,192,0)" },
      { pos: 0.5, color: "rgb(255,128,0)" },
      { pos: 0.75, color: "rgb(255,64,0)" },
      { pos: 1, color: "rgb(255,0,0)" },
   ].forEach((s) => gradient.addColorStop(s.pos, s.color));
   ctx.fillStyle = gradient;
   ctx.fillRect(gradientX, gradientY, gradientWidth, colorBarHeight);
   ctx.strokeStyle = "rgba(0,0,0,0.3)";
   ctx.lineWidth = 1;
   ctx.strokeRect(gradientX, gradientY, gradientWidth, colorBarHeight);

   ctx.fillStyle = "#666";
   ctx.font = "11px Arial";
   ctx.textAlign = "left";
   ctx.fillText(
      `${Math.round(minPrice).toLocaleString("da-DK")} kr`,
      gradientX,
      gradientY + colorBarHeight + 12,
   );
   ctx.textAlign = "right";
   ctx.fillText(
      `${Math.round(maxPrice).toLocaleString("da-DK")} kr`,
      gradientX + gradientWidth,
      gradientY + colorBarHeight + 12,
   );

   currentY += colorBarHeight + 25;

   if (haveArea) {
      currentY += 8;
      ctx.fillStyle = "#333";
      ctx.font = "12px Arial";
      ctx.textAlign = "left";
      ctx.fillText("Areal (m²)", bgX + 12, currentY);
      currentY += 18;

      const circleY = currentY + 4;
      const circleSpacing = 55;
      const circles = [
         { radius: 5, area: minArea! },
         { radius: 11, area: (minArea! + maxArea!) / 2 },
         { radius: maxCircleRadius, area: maxArea! },
      ];
      const totalCirclesWidth =
         (circles.length - 1) * circleSpacing + circles[0].radius * 2;
      const startX =
         bgX + (legendWidth - totalCirclesWidth) / 2 + circles[0].radius;
      circles.forEach((c, i) => {
         const cx = startX + i * circleSpacing;
         ctx.fillStyle = "rgba(58,142,135,0.3)";
         ctx.beginPath();
         ctx.arc(cx, circleY, c.radius, 0, Math.PI * 2);
         ctx.fill();
         ctx.strokeStyle = "#3a8e87";
         ctx.lineWidth = 1.5;
         ctx.stroke();
         ctx.fillStyle = "#666";
         ctx.font = "10px Arial";
         ctx.textAlign = "center";
         ctx.fillText(
            `${Math.round(c.area).toLocaleString("da-DK")} m²`,
            cx,
            circleY + maxCircleRadius + 12,
         );
      });
   }
   return { width: legendWidth, height: totalHeight };
}

// ============================================================================
// HELPER FUNCTIONS - Map layers
// ============================================================================

function setupMaskLayer(map: MaplibreMap, bufferGeometry: any) {
   if (!bufferGeometry) {
      if (map.getSource(MASK_SOURCE_ID)) {
         const source = map.getSource(MASK_SOURCE_ID) as GeoJSONSource;
         if (source) {
            source.setData({
               type: "FeatureCollection",
               features: [],
            } as any);
         }
      }
      return;
   }

   try {
      if (!map.getSource(MASK_SOURCE_ID)) {
         map.addSource(MASK_SOURCE_ID, {
            type: "geojson",
            data: {
               type: "FeatureCollection",
               features: [],
            },
         });
      }

      if (!map.getLayer(MASK_LAYER_ID)) {
         const maskLayerSpec: any = {
            id: MASK_LAYER_ID,
            type: "fill",
            source: MASK_SOURCE_ID,
            paint: {
               "fill-color": "#000000",
               "fill-opacity": 0.15,
            },
         };
         const outlineLayerId = "jpb-export-search-area-outline";
         if (map.getLayer(outlineLayerId)) {
            map.addLayer(maskLayerSpec, outlineLayerId);
         } else {
            map.addLayer(maskLayerSpec);
         }
      }

      const worldBbox: [number, number, number, number] = [-180, -90, 180, 90];
      const worldPolygon = bboxPolygon(worldBbox);
      const bufferFeature = JPBManager.toCombinedSearchGeometry(bufferGeometry);
      if (!bufferFeature) return;

      const maskGeometry = difference(
         featureCollection([worldPolygon, bufferFeature]),
      );

      if (maskGeometry) {
         const maskFeatureCollection = {
            type: "FeatureCollection",
            features: [maskGeometry],
         };
         const source = map.getSource(MASK_SOURCE_ID) as GeoJSONSource;
         if (source) {
            source.setData(maskFeatureCollection as any);
         }
      }
   } catch (error) {
      console.warn("Error creating mask layer:", error);
   }
}

// ============================================================================
// HELPER FUNCTIONS - Canvas drawing
// ============================================================================

const JORDBUND_LEGEND_URL =
   "https://geodata.fvm.dk:443/geoserver/ows?SERVICE=WMS&REQUEST=GetLegendGraphic&VERSION=1.3.0&FORMAT=image/png&SLD_VERSION=1.1.0&LAYER=Jordbunds_og_terraenforhold:Jordbundskort_2024&STYLE=";

async function loadJordbundLegendImage(): Promise<HTMLImageElement | undefined> {
   try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      await new Promise<void>((resolve, reject) => {
         img.onload = () => resolve();
         img.onerror = () =>
            reject(new Error("Failed to load jordbund legend image"));
         img.src = JORDBUND_LEGEND_URL;
      });
      return img;
   } catch (error) {
      console.warn("Failed to load jordbund legend:", error);
      return undefined;
   }
}

function measureJordbundLegendSize(img: HTMLImageElement): {
   width: number;
   height: number;
} {
   const bgPadding = 8;
   const legendWidth = Math.min(img.width, 200);
   const legendHeight = Math.min(img.height, 400);
   const scale = Math.min(legendWidth / img.width, legendHeight / img.height);
   const scaledWidth = img.width * scale;
   const scaledHeight = img.height * scale;
   return {
      width: scaledWidth + bgPadding * 2,
      height: scaledHeight + bgPadding * 2,
   };
}

function drawJordbundLegendAt(
   ctx: CanvasRenderingContext2D,
   x: number,
   y: number,
   img: HTMLImageElement,
): { width: number; height: number } {
   const bgPadding = 8;
   const legendWidth = Math.min(img.width, 200);
   const legendHeight = Math.min(img.height, 400);
   const scale = Math.min(legendWidth / img.width, legendHeight / img.height);
   const scaledWidth = img.width * scale;
   const scaledHeight = img.height * scale;
   const legendX = x + bgPadding;
   const legendY = y + bgPadding;
   const bgWidth = scaledWidth + bgPadding * 2;
   const bgHeight = scaledHeight + bgPadding * 2;

   ctx.fillStyle = "rgba(255, 255, 255, 0.98)";
   ctx.beginPath();
   ctx.roundRect(x, y, bgWidth, bgHeight, 8);
   ctx.fill();

   ctx.strokeStyle = "rgba(0, 0, 0, 0.1)";
   ctx.lineWidth = 1;
   ctx.beginPath();
   ctx.roundRect(x, y, bgWidth, bgHeight, 8);
   ctx.stroke();

   ctx.drawImage(img, legendX, legendY, scaledWidth, scaledHeight);
   return { width: bgWidth, height: bgHeight };
}

// ============================================================================
// MARK: V2 REPORT — Subject polygon close-up map
// ============================================================================

/**
 * Renders a close-up of the analyzed subject polygon, highlighted in the same
 * mustard yellow that the live JPB polygon analyzer uses for property outlines
 * (`JPB_ANALYZER_PROPERTY_OUTLINE_COLOR = '#d6c251'`), without comparison points.
 */
export async function renderJPBSubjectPolygonImage(options: {
   mapStyle: any;
   polygonGeoJson: any;
}): Promise<Uint8Array | undefined> {
   const { mapStyle, polygonGeoJson } = options;
   if (!polygonGeoJson) return undefined;

   const { exportMap, container, exportMapFacade } =
      await createExportMap(mapStyle);
   try {
      const SUBJECT_SOURCE_ID = "jpb-export-subject-polygon";
      const SUBJECT_FILL_ID = "jpb-export-subject-polygon-fill";
      const SUBJECT_OUTLINE_ID = "jpb-export-subject-polygon-outline";

      // Mustard yellow shared with the live polygon analyzer outline so the
      // exported subject polygon matches the on-screen JPB visual language.
      const JPB_POLYGON_COLOR = "#d6c251";

      exportMap.addSource(SUBJECT_SOURCE_ID, {
         type: "geojson",
         data: feature(polygonGeoJson) as any,
      } as any);
      exportMap.addLayer({
         id: SUBJECT_FILL_ID,
         type: "fill",
         source: SUBJECT_SOURCE_ID,
         paint: { "fill-color": JPB_POLYGON_COLOR, "fill-opacity": 0.28 },
      } as any);
      exportMap.addLayer({
         id: SUBJECT_OUTLINE_ID,
         type: "line",
         source: SUBJECT_SOURCE_ID,
         paint: { "line-color": JPB_POLYGON_COLOR, "line-width": 3 },
      } as any);

      const polygonBbox = bbox(feature(polygonGeoJson) as any);
      exportMap.fitBounds(polygonBbox as any, { padding: 60, maxZoom: 17 });
      await waitForMapReady(exportMap);
      return await captureMapImage(exportMap);
   } finally {
      cleanupExportMap(exportMapFacade, container);
   }
}

// ============================================================================
// MARK: V2 REPORT — All-points comparison map (no checked-only filter, no heatmap)
// ============================================================================

/**
 * Renders the comparison map showing ALL reference properties (excluded + included),
 * styled the same way they appear on the live map (green / black / grey pins, with houses).
 */
export async function renderJPBAllPointsMapImage(options: {
   mapStyle: any;
   referenceProperties: JPBReferenceProperty[];
   checkedDocumentIds: string[];
   bufferGeometry?: any | null;
}): Promise<Uint8Array | undefined> {
   const { mapStyle, referenceProperties, checkedDocumentIds, bufferGeometry } =
      options;
   if (!referenceProperties.length) return undefined;

   const { exportMap, container, exportMapFacade } =
      await createExportMap(mapStyle);
   try {
      const checked = new Set<string>(checkedDocumentIds);
      // Pass ALL reference properties (not just checked) — the map layers will style
      // included vs. excluded points correctly based on the `checked` set.
      setupJPBMapLayers(
         exportMap,
         referenceProperties,
         checked,
         PriceMode.Standard,
         AreaUnit.SquareMeter,
         {
            onFeatureClick: () => {
               /* no-op in export map */
            },
         },
      );

      if (bufferGeometry) {
         addBufferLayer(exportMap, bufferGeometry);
         setupMaskLayer(exportMap, bufferGeometry);
      }

      await fitMapToFeatures(exportMap, referenceProperties, { bufferGeometry });
      await waitForMapReady(exportMap);

      const mapCanvas = exportMap.getCanvas();
      const legendSize = measureJPBLegendSize();
      const { canvas: compositeCanvas, ctx } = createCompositeCanvas(
         mapCanvas,
         0,
         legendSize.height + LEGEND_GAP,
      );
      drawJPBLegendAt(ctx, 12, mapCanvas.height + LEGEND_GAP);
      return await captureCanvasImage(compositeCanvas);
   } finally {
      cleanupExportMap(exportMapFacade, container);
   }
}

// ============================================================================
// MARK: V2 REPORT — DOM element snapshot (statistics card, polygon analyzer)
// ============================================================================

interface RenderElementOptions {
   backgroundColor?: string;
   pixelRatio?: number;
}

async function renderElementToPng(
   element: HTMLElement,
   options: RenderElementOptions,
): Promise<Uint8Array | undefined> {
   const dataUrl = await toPng(element, {
      backgroundColor: options.backgroundColor ?? "#ffffff",
      pixelRatio: options.pixelRatio ?? 2,
      cacheBust: true,
      // Skip elements that are floating tooltips / portals to avoid cross-origin or off-screen issues.
      filter: (node) => {
         if (!(node instanceof HTMLElement)) return true;
         const cls = node.className;
         if (typeof cls === "string" && cls.includes("ViewButtonToolTip"))
            return false;
         return true;
      },
   });
   const base64 = dataUrl.split(",")[1];
   if (!base64) return undefined;
   const binary = atob(base64);
   const bytes = new Uint8Array(binary.length);
   for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
   return bytes;
}

/**
 * Renders an arbitrary live DOM element to a PNG image.
 *
 * Prefer {@link renderClonedElementImage} for report generation: cloning into
 * a `JPBCaptureStage` first decouples the snapshot from the live UI, so the
 * user can close / minimize the JPB window during the (long) report
 * generation without producing blank or partial images.
 */
export async function renderDomElementImage(
   element: HTMLElement,
   options: RenderElementOptions = {},
): Promise<Uint8Array | undefined> {
   if (!element) return undefined;
   try {
      return await renderElementToPng(element, options);
   } catch (e) {
      console.warn("renderDomElementImage failed", e);
      return undefined;
   }
}

/**
 * Renders a cloned element that has been attached to a `JPBCaptureStage`.
 *
 * Functionally identical to {@link renderDomElementImage} but exists as its
 * own entry point so callers explicitly opt into the off-screen capture
 * pipeline. The clone must be attached to the document at call time so
 * `getComputedStyle` returns real values during `html-to-image` traversal.
 */
export async function renderClonedElementImage(
   clone: HTMLElement,
   options: RenderElementOptions = {},
): Promise<Uint8Array | undefined> {
   if (!clone) return undefined;
   if (!clone.isConnected) {
      console.warn(
         "renderClonedElementImage: clone is not attached to the document",
      );
      return undefined;
   }
   try {
      return await renderElementToPng(clone, options);
   } catch (e) {
      console.warn("renderClonedElementImage failed", e);
      return undefined;
   }
}

declare global {
   interface CanvasRenderingContext2D {
      roundRect(
         x: number,
         y: number,
         width: number,
         height: number,
         radius: number,
      ): void;
   }
}

if (!CanvasRenderingContext2D.prototype.roundRect) {
   CanvasRenderingContext2D.prototype.roundRect = function (
      x: number,
      y: number,
      width: number,
      height: number,
      radius: number,
   ) {
      this.beginPath();
      this.moveTo(x + radius, y);
      this.lineTo(x + width - radius, y);
      this.quadraticCurveTo(x + width, y, x + width, y + radius);
      this.lineTo(x + width, y + height - radius);
      this.quadraticCurveTo(
         x + width,
         y + height,
         x + width - radius,
         y + height,
      );
      this.lineTo(x + radius, y + height);
      this.quadraticCurveTo(x, y + height, x, y + height - radius);
      this.lineTo(x, y + radius);
      this.quadraticCurveTo(x, y, x + radius, y);
      this.closePath();
   };
}
